import React, { useState } from 'react';
import { CheckCircle, XCircle, Pencil, Check, X } from 'lucide-react';
import { formatEuro, formatNumber } from '../../../utils/formatters';
import { ENERGINO_HAPPY, ENERGINO_SAD } from '../../../utils/constants';

/**
 * Riga risparmio con mascotte Energino + proiezione annuale 12 mesi.
 * @param {Object} props
 * @param {Object} props.analysis - analisi completa
 * @param {Function} [props.onUpdateAnnualConsumption] - callback per override consumo annuo
 */
export const SavingsRow = ({ analysis, onUpdateAnnualConsumption }) => {
  const annualPositive = analysis.annual_projection?.is_positive;
  const hasAnnual = !!analysis.annual_projection;
  const billPositive = analysis.is_positive;
  const showPositive = hasAnnual ? annualPositive : billPositive;

  return (
    <>
      {/* Energino + etichetta risparmio/rincaro */}
      <div className={`p-4 ${showPositive ? 'bg-[#E8F5E9]' : 'bg-orange-50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              style={{
                width: '64px', height: '64px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                backgroundColor: showPositive ? 'rgba(76,175,80,0.15)' : 'rgba(251,146,60,0.15)',
                border: showPositive ? '2px solid rgba(76,175,80,0.3)' : '2px solid rgba(251,146,60,0.3)',
              }}
            >
              <img
                src={showPositive ? ENERGINO_HAPPY : ENERGINO_SAD}
                alt="Energino"
                style={{ maxWidth: '48px', maxHeight: '48px', width: 'auto', height: 'auto' }}
                data-testid="energino-mascot"
                data-pdf-img
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                {showPositive ? <CheckCircle className="w-5 h-5 text-[#4CAF50]" /> : <XCircle className="w-5 h-5 text-orange-500" />}
                <span className={`font-medium ${showPositive ? 'text-[#4CAF50]' : 'text-orange-600'}`}>
                  {hasAnnual ? (annualPositive ? 'RISPARMIO ANNUO' : 'RINCARO ANNUO') : (billPositive ? 'RISPARMIO' : 'DIFFERENZA')}
                </span>
              </div>
              {hasAnnual && billPositive !== annualPositive && (
                <p className="text-xs text-[#5C6E5C] mt-1">
                  {billPositive && !annualPositive
                    ? `Bolletta: -${formatEuro(Math.abs(analysis.savings_amount))}, ma su 12 mesi +${formatEuro(Math.abs(analysis.annual_projection.savings_annual))}`
                    : `Bolletta: +${formatEuro(Math.abs(analysis.savings_amount))}, ma su 12 mesi -${formatEuro(Math.abs(analysis.annual_projection.savings_annual))}`
                  }
                </p>
              )}
            </div>
          </div>
          <SavingsAmount analysis={analysis} hasAnnual={hasAnnual} annualPositive={annualPositive} billPositive={billPositive} />
        </div>
      </div>

      {/* Proiezione annuale 12 mesi */}
      {hasAnnual && <AnnualProjection projection={analysis.annual_projection} onUpdateAnnualConsumption={onUpdateAnnualConsumption} />}
    </>
  );
};

/** Importo risparmio a destra */
const SavingsAmount = ({ analysis, hasAnnual, annualPositive, billPositive }) => (
  <div className="text-right">
    {hasAnnual ? (
      <>
        <p className={`text-2xl font-bold ${annualPositive ? 'text-[#4CAF50]' : 'text-orange-600'}`}>
          {annualPositive ? '-' : '+'}{formatEuro(Math.abs(analysis.annual_projection.savings_annual))}/anno
        </p>
        <p className="text-sm text-[#5C6E5C]">{formatNumber(Math.abs(analysis.annual_projection.savings_annual_percent), 1)}%</p>
      </>
    ) : (
      <>
        <p className={`text-2xl font-bold ${billPositive ? 'text-[#4CAF50]' : 'text-orange-600'}`}>
          {billPositive ? '-' : '+'}{formatEuro(Math.abs(analysis.savings_amount))}
        </p>
        <p className="text-sm text-[#5C6E5C]">{formatNumber(Math.abs(analysis.savings_percent), 1)}%</p>
      </>
    )}
  </div>
);

/** Box proiezione 12 mesi con delta */
const AnnualProjection = ({ projection, onUpdateAnnualConsumption }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const deltas = [
    { label: 'Delta Consumi', value: projection.delta_consumi_annual },
    { label: 'Delta Quota Fissa', value: projection.delta_fissa_annual },
    { label: 'Delta IVA', value: projection.delta_iva_annual },
  ];

  const startEdit = () => {
    setValue(String(Math.round(projection.annual_consumption || 0)));
    setEditing(true);
  };

  const save = async () => {
    const n = parseFloat(String(value).replace(',', '.'));
    if (!n || n <= 0) return;
    setSaving(true);
    try {
      await onUpdateAnnualConsumption(n);
      setEditing(false);
    } catch (_e) {
      // errore già notificato nel hook
    }
    setSaving(false);
  };

  return (
    <div className="p-4 border-t border-[#DCE3DC] bg-gradient-to-r from-[#F4F7F4] to-white" data-testid="annual-projection">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <svg className="w-5 h-5 text-[#4CAF50]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="font-semibold text-sm text-[#1B291B]">Proiezione su 12 mesi</span>
        {editing ? (
          <div className="flex items-center gap-1" data-testid="annual-consumption-editor">
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-28 px-2 py-1 text-xs border border-[#4CAF50] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4CAF50]/30"
              autoFocus
              disabled={saving}
              data-testid="annual-consumption-input"
            />
            <span className="text-xs text-[#5C6E5C]">{projection.consumption_unit}/anno</span>
            <button
              onClick={save}
              disabled={saving}
              className="p-1 rounded-md bg-[#4CAF50] text-white hover:bg-[#3D8C40] disabled:opacity-50"
              title="Salva"
              data-testid="annual-consumption-save"
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="p-1 rounded-md bg-white border border-[#DCE3DC] text-[#5C6E5C] hover:bg-[#F4F7F4]"
              title="Annulla"
              data-testid="annual-consumption-cancel"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <span className="text-[10px] bg-[#E8F5E9] text-[#4CAF50] px-2 py-0.5 rounded-full inline-flex items-center gap-1.5" data-testid="annual-consumption-badge">
            {formatNumber(projection.annual_consumption, 0)} {projection.consumption_unit}/anno
            {onUpdateAnnualConsumption && (
              <button
                onClick={startEdit}
                className="ml-1 p-0.5 rounded hover:bg-[#4CAF50]/20"
                title="Modifica consumo annuo"
                data-testid="annual-consumption-edit-btn"
              >
                <Pencil className="w-3 h-3" />
              </button>
            )}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        {deltas.map((d) => (
          <div key={d.label} className="p-2 rounded-lg bg-white border border-[#DCE3DC]">
            <p className="text-[10px] text-[#5C6E5C] mb-0.5">{d.label}</p>
            <p className={`text-sm font-semibold ${d.value >= 0 ? 'text-[#4CAF50]' : 'text-orange-600'}`}>
              {d.value >= 0 ? '-' : '+'}{formatEuro(Math.abs(d.value))}
            </p>
          </div>
        ))}
      </div>
      <div className={`mt-3 p-3 rounded-xl text-center ${projection.is_positive ? 'bg-[#E8F5E9]' : 'bg-orange-50'}`}>
        <p className="text-xs text-[#5C6E5C] mb-1">
          {projection.is_positive ? 'RISPARMIO ANNUO STIMATO' : 'RINCARO ANNUO STIMATO'}
        </p>
        <p className={`text-xl font-bold ${projection.is_positive ? 'text-[#4CAF50]' : 'text-orange-600'}`}>
          {projection.is_positive ? '-' : '+'}{formatEuro(Math.abs(projection.savings_annual))}
          <span className="text-sm font-normal ml-1">({formatNumber(Math.abs(projection.savings_annual_percent), 1)}%)</span>
        </p>
      </div>
    </div>
  );
};
